/**
 * i18n resources for the Hotel setup feature slice.
 *
 * Namespace: `hotelSetup`. Exposed as `{ namespace, resources: { en, bg, de, fr } }`
 * (the wrapped shape) so the app shell can merge it via `fromWrapped` and tests
 * can pass it straight to `renderWithProviders({ i18nResources })`.
 *
 * Key parity is enforced by `@hostesa/i18n` (a missing key THROWS in test +
 * production), so every locale below carries the same key set. Non-`en` locales
 * are seeded as English aliases until a real translation exists — the documented
 * temporary state.
 */

const en = {
  eyebrow: 'Settings',
  title: 'Hotel setup',
  description:
    'Operational facts the AI receptionist uses to answer guests. Saved facts publish to the AI knowledge and count toward activation.',
  loading: 'Loading hotel setup…',
  errorTitle: 'Could not load hotel setup',
  errorDescription: 'There was a problem loading the hotel facts. Please try again.',
  readOnlyNote: 'You have read-only access to this hotel.',
  required: {
    title: 'Required for activation',
    description: 'These four facts must be set before the hotel can go live on the voice line.',
  },
  goodToHave: {
    title: 'Good to have',
    description: 'Facts guests commonly call about. Optional, but they make the AI more helpful.',
  },
  activationComplete: 'Activation requirements complete',
  status: {
    set: 'Set',
    missing: 'Missing',
  },
  save: 'Save',
  saving: 'Saving…',
  saved: 'Saved.',
  saveError: 'Could not save. Please try again.',
  clear: 'Clear',
  clearing: 'Clearing…',
  clearError: 'Could not clear. Please try again.',
  impersonationNote:
    'Disabled during an impersonation session — sensitive changes are blocked while acting as this user.',
  categories: {
    cancellation_policy: {
      label: 'Cancellation policy',
      description: 'How far ahead a guest can cancel and any fees that apply.',
    },
    check_in_time: {
      label: 'Check-in time',
      description: 'The earliest time a guest can check in (e.g. 15:00).',
    },
    check_out_time: {
      label: 'Check-out time',
      description: 'The latest time a guest must check out by (e.g. 11:00).',
    },
    transfer_number: {
      label: 'Transfer number',
      description: 'The phone number the AI transfers callers to for the front desk.',
    },
    breakfast_hours: {
      label: 'Breakfast hours',
      description: 'When breakfast is served (e.g. 07:00–10:30).',
    },
    dinner_hours: {
      label: 'Dinner hours',
      description: 'When dinner is served in the restaurant.',
    },
    spa_hours: {
      label: 'Spa hours',
      description: 'Opening hours for the spa / wellness area.',
    },
    pool_hours: {
      label: 'Pool hours',
      description: 'When the pool is open to guests.',
    },
    parking: {
      label: 'Parking',
      description: 'Parking options, availability, and any charges.',
    },
    location_directions: {
      label: 'Directions & location',
      description: 'How to reach the hotel, and its address / GPS coordinates.',
    },
    lost_and_found: {
      label: 'Lost & found',
      description: 'How guests can report or reclaim lost items.',
    },
    pets_policy: {
      label: 'Pets policy',
      description: 'Whether pets are allowed and any conditions or fees.',
    },
  },
};

type HotelSetupResources = typeof en;

// Non-English locales reuse the English copy until translated (key parity).
const bg: HotelSetupResources = en;
const de: HotelSetupResources = en;
const fr: HotelSetupResources = en;

export const namespace = 'hotelSetup' as const;

export const resources: Record<'en' | 'bg' | 'de' | 'fr', { hotelSetup: HotelSetupResources }> = {
  en: { hotelSetup: en },
  bg: { hotelSetup: bg },
  de: { hotelSetup: de },
  fr: { hotelSetup: fr },
};

export const hotelSetupI18n = { namespace, resources } as const;
